import { HIDE_ISO_14067_UI } from './uiVisibility';

export const REPORTING_TAB_LABELS = HIDE_ISO_14067_UI
  ? (['BRSR', 'ISO 14064', 'CBAM'] as const)
  : (['BRSR', 'ISO 14064', 'ISO 14067', 'CBAM'] as const);

export type ReportingTabLabel = (typeof REPORTING_TAB_LABELS)[number];

/** File tabs only. Compliance Overview owns readiness; these tabs download the report. */
export const REPORTING_TAB = {
  BRSR: 0,
  ISO_14064: 1,
  ISO_14067: HIDE_ISO_14067_UI ? -1 : 2,
  CBAM: HIDE_ISO_14067_UI ? 2 : 3,
} as const;

export const REPORTING_TAB_COUNT = REPORTING_TAB_LABELS.length;

export const REPORTING_TAB_SLUGS = {
  /** Disclosure reports nav. Lands on the BRSR file. */
  overview: REPORTING_TAB.BRSR,
  brsr: REPORTING_TAB.BRSR,
  iso14064: REPORTING_TAB.ISO_14064,
  iso: REPORTING_TAB.ISO_14064,
  iso14067: REPORTING_TAB.ISO_14067,
  cbam: REPORTING_TAB.CBAM,
} as const;

export type ReportingTabSlug = keyof typeof REPORTING_TAB_SLUGS;

/**
 * Old numeric ?tab= values from when Overview was index 0.
 * New links write slugs so a fresh ?tab=1 is not read as the old BRSR index.
 */
const LEGACY_NUMERIC_TAB: Record<number, number> = HIDE_ISO_14067_UI
  ? {
      0: REPORTING_TAB.BRSR,
      1: REPORTING_TAB.BRSR,
      2: REPORTING_TAB.ISO_14064,
      3: REPORTING_TAB.CBAM,
      4: REPORTING_TAB.CBAM,
    }
  : {
      0: REPORTING_TAB.BRSR,
      1: REPORTING_TAB.BRSR,
      2: REPORTING_TAB.ISO_14064,
      3: REPORTING_TAB.ISO_14067,
      4: REPORTING_TAB.CBAM,
    };

/** Resolves ?tab= slugs and legacy numbers onto a visible file tab. */
export function reportingTabIndexFromParam(raw: string | null | undefined): number {
  const trimmed = raw?.trim().toLowerCase() ?? '';
  if (!trimmed) {
    return REPORTING_TAB.BRSR;
  }
  if (Object.prototype.hasOwnProperty.call(REPORTING_TAB_SLUGS, trimmed)) {
    const index = REPORTING_TAB_SLUGS[trimmed as ReportingTabSlug];
    if (index < 0) {
      return REPORTING_TAB.ISO_14064;
    }
    return index;
  }
  if (/^\d+$/.test(trimmed)) {
    const legacy = LEGACY_NUMERIC_TAB[Number(trimmed)];
    if (legacy !== undefined && legacy >= 0) {
      return legacy;
    }
  }
  return REPORTING_TAB.BRSR;
}

/** Query value written when the user changes tabs. BRSR stays on /reporting. */
export function reportingTabParamForIndex(index: number): string | null {
  if (index === REPORTING_TAB.BRSR) {
    return null;
  }
  if (index === REPORTING_TAB.ISO_14064) {
    return 'iso14064';
  }
  if (REPORTING_TAB.ISO_14067 >= 0 && index === REPORTING_TAB.ISO_14067) {
    return 'iso14067';
  }
  if (index === REPORTING_TAB.CBAM) {
    return 'cbam';
  }
  return null;
}

/** Clamps a numeric tab index onto a visible file tab. */
export function clampReportingTab(tab: number): number {
  if (!Number.isFinite(tab) || tab < 0) {
    return REPORTING_TAB.BRSR;
  }
  const floored = Math.floor(tab);
  if (floored >= REPORTING_TAB_COUNT) {
    return REPORTING_TAB_COUNT - 1;
  }
  return floored;
}

/** Builds disclosure-report paths. BRSR is /reporting; other files use ?tab=slug. */
export function reportingTabHref(slug: ReportingTabSlug): string {
  const param = reportingTabParamForIndex(reportingTabIndexFromParam(slug));
  if (!param) {
    return '/reporting';
  }
  return `/reporting?tab=${param}`;
}
